import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

/**
 * What the composer did with the notes it was handed.
 *
 * The composer reads the reply box as two channels: prose meant for the
 * client, and directives meant for the agent ("hey agent, pull their set rate
 * against the vertical"). It executes the directives with real tool calls and
 * folds the answers into the draft. That is useful and also invisible: the
 * staff member gets back a paragraph of numbers with no way to tell whether
 * the agent looked them up or invented them.
 *
 * So this panel shows the work. Which directives it read out of the notes,
 * which tools it actually ran, anything it could not do, and any numeric claim
 * the auditor could not tie back to the data. Nothing here sends on its own —
 * it exists to be read before a human hits send.
 */

// Tool names are snake_case internals; a chip reading "fetch_vertical_
// benchmark" is noise. Strip the prefix and let the words do the work.
const prettyToolName = (name) =>
  String(name || '')
    .replace(/^fetch_/, '')
    .replace(/_/g, ' ');

const CLAIM_SEVERITY = {
  inaccurate: 'error',
  risky: 'warning',
};

export default function ComposerReport({ report, onDismiss }) {
  if (!report) return null;

  const directives = report.directives || [];
  const toolCalls = report.tool_calls || [];
  const notes = (report.notes || '').trim();
  const audit = report.claim_audit;

  if (!directives.length && !toolCalls.length && !notes && !audit) return null;

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {directives.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Read as instructions to the agent (kept out of the email)
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {directives.map((directive) => (
              <Chip key={directive} label={directive} size="small" variant="outlined" />
            ))}
          </Stack>
        </Stack>
      )}

      {toolCalls.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Looked up
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {toolCalls.map((call, idx) => (
              <Chip
                key={`${call.name}-${idx}`}
                label={prettyToolName(call.name)}
                size="small"
                color="info"
                variant="outlined"
              />
            ))}
          </Stack>
        </Stack>
      )}

      {notes && (
        <Alert severity="info" onClose={onDismiss} sx={{ py: 0.5 }}>
          {notes}
        </Alert>
      )}

      {audit && (
        <Alert severity={CLAIM_SEVERITY[audit.verdict] || 'warning'} sx={{ py: 0.5 }}>
          <AlertTitle sx={{ mb: 0.5 }}>Check these numbers before sending</AlertTitle>
          {audit.summary && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {audit.summary}
            </Typography>
          )}
          <Stack component="ul" spacing={0.25} sx={{ pl: 2, m: 0 }}>
            {(audit.claims || []).map((claim, idx) => (
              <Typography component="li" variant="body2" key={`${claim.claimed_text}-${idx}`}>
                {claim.claimed_text}
                {claim.note ? ` — ${claim.note}` : ''}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

ComposerReport.propTypes = {
  report: PropTypes.shape({
    directives: PropTypes.arrayOf(PropTypes.string),
    tool_calls: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      }),
    ),
    notes: PropTypes.string,
    claim_audit: PropTypes.shape({
      verdict: PropTypes.string,
      summary: PropTypes.string,
      claims: PropTypes.arrayOf(PropTypes.object),
    }),
  }),
  onDismiss: PropTypes.func,
};

ComposerReport.defaultProps = {
  report: null,
  onDismiss: undefined,
};
